import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/useApp';
import { motion } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';
import { chatService, realtimeChatService } from '../services/firebaseService';
import { MoreVertical, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';

const ChatDetail = () => {
  const navigate = useNavigate();
  const { language, user } = useApp();
  const [activeTab, setActiveTab] = useState('all');
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showMenu, setShowMenu] = useState(null); // For dropdown menu

  const translations = {
    english: {
      title: 'Chats',
      subtitle: 'Connect with your lawyers',
      all: 'All',
      active: 'Active',
      completed: 'Completed',
      noChats: 'No chats yet',
      noChatsDesc: 'Start a conversation with your lawyers after booking a consultation',
      lastMessage: 'Last message',
      activeChat: 'Active',
      completedChat: 'Completed',
      viewChat: 'View Chat',
      startChat: 'Start Chat',
      loading: 'Loading chats...',
      error: 'Error loading chats',
      retry: 'Retry',
      deleteChat: 'Delete Chat',
      confirmDelete: 'Are you sure you want to delete this chat?'
    },
    hindi: {
      title: 'चैट',
      subtitle: 'अपने वकीलों से जुड़ें',
      all: 'सभी',
      active: 'सक्रिय',
      completed: 'पूर्ण',
      noChats: 'अभी तक कोई चैट नहीं',
      noChatsDesc: 'परामर्श बुक करने के बाद अपने वकीलों के साथ बातचीत शुरू करें',
      lastMessage: 'अंतिम संदेश',
      activeChat: 'सक्रिय',
      completedChat: 'पूर्ण',
      viewChat: 'चैट देखें',
      startChat: 'चैट शुरू करें',
      loading: 'चैट लोड हो रहे हैं...',
      error: 'चैट लोड करने में त्रुटि',
      retry: 'पुनः प्रयास करें',
      deleteChat: 'चैट हटाएं',
      confirmDelete: 'क्या आप वाकई इस चैट को हटाना चाहते हैं?'
    }
  };

  const t = translations[language] || translations.english;

  const tabs = [
    { id: 'all', label: t.all },
    { id: 'active', label: t.active },
    { id: 'completed', label: t.completed }
  ];

  // Load chats from database
  const loadChats = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      const result = await chatService.getUserChatRooms(user.id);
      
      if (result.success) {
        // Transform data to match existing format
        const transformedChats = result.data.map(chat => ({
          id: chat.id,
          lawyer: {
            id: chat.lawyer?.id,
            name: chat.lawyer?.user_profiles?.full_name || 'Lawyer',
            specialization: chat.lawyer?.specialization?.join(', ') || 'General Law',
            profilePic: chat.lawyer?.user_profiles?.profile_picture || 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face',
            rating: chat.lawyer?.average_rating || 0,
            experience: chat.lawyer?.experience_years || 0,
            city: chat.lawyer?.city || 'Location not specified'
          },
          lastMessage: chat.last_message?.[0]?.message_text || 'No messages yet',
          lastMessageTime: formatTime(chat.last_message?.[0]?.created_at) || 'Just now',
          unreadCount: 0, // Will be calculated separately
          status: 'active',
          statusLabel: t.activeChat,
          bookingDate: chat.created_at,
          bookingTime: '10:00 AM',
          consultationType: 'Video Call'
        }));
        
        setChats(transformedChats);
      } else {
        setError(result.error);
        toast.error(result.error);
      }
    } catch (error) {
      console.error('Error loading chats:', error);
      setError('Failed to load chats');
      toast.error('Failed to load chats');
    } finally {
      setLoading(false);
    }
  };

  // Load chats on mount and when user changes
  useEffect(() => {
    loadChats();
  }, [user?.id]);

  // Subscribe to real-time updates
  useEffect(() => {
    if (!user?.id) return;

    const channel = realtimeChatService.subscribeToUserChatRooms(user.id, (payload) => {
      // Refresh chats when there are changes
      loadChats();
    });

    return () => {
      realtimeChatService.unsubscribe(channel);
    };
  }, [user?.id]);

  // Format time helper
  const formatTime = (dateString) => {
    if (!dateString) return 'Just now';
    
    let date;
    
    // Handle Firebase Timestamp objects
    if (dateString && typeof dateString === 'object' && dateString.seconds) {
      date = new Date(dateString.seconds * 1000);
    } else if (dateString && typeof dateString === 'object' && dateString.toDate) {
      // Handle Firebase Timestamp with toDate method
      date = dateString.toDate();
    } else {
      // Handle regular Date objects or ISO strings
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) return 'Just now';
    
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'active':
        return 'status-warning';
      case 'completed':
        return 'status-neutral';
      default:
        return 'status-info';
    }
  };

  const filteredChats = chats.filter(chat => {
    if (activeTab === 'all') return true;
    return chat.status === activeTab;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const handleViewChat = (chat) => {
    // Navigate to chat conversation with real chat room ID
    navigate(`/chat-conversation/${chat.id}`, { 
      state: { 
        chat: {
          id: chat.id,
          lawyer: chat.lawyer,
          chatRoomId: chat.id
        } 
      } 
    });
  };

  const handleMenuClick = (e, chatId) => {
    e.stopPropagation();
    setShowMenu(showMenu === chatId ? null : chatId);
  };

  const handleDeleteChat = async (chatId) => {
    // Show confirmation dialog
    if (!window.confirm(t.confirmDelete)) {
      return;
    }
    
    try {
      // Remove from database if user is logged in
      if (user?.id) {
        // You can add database deletion logic here if needed
        console.log('🗑️ Deleting chat from database:', chatId);
      }
      
      // Remove from localStorage
      const allChats = JSON.parse(localStorage.getItem('userChats') || '[]');
      const updatedChats = allChats.filter(chat => chat.id !== chatId);
      localStorage.setItem('userChats', JSON.stringify(updatedChats));
      
      // Remove chat messages from localStorage
      localStorage.removeItem(`chat_messages_${chatId}`);
      
      // Update the local state to reflect the change
      setChats(prevChats => prevChats.filter(chat => chat.id !== chatId));
      
      // Close menu
      setShowMenu(null);
      
      console.log('✅ Chat deleted successfully:', chatId);
      toast.success('Chat deleted successfully');
    } catch (error) {
      console.error('❌ Error deleting chat:', error);
      toast.error('Failed to delete chat');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Section */}
      <div className="px-4 pt-6 pb-4">
        <div className="flex items-center gap-4 mb-4">
            <button
              onClick={() => navigate(-1)}
            className="flex items-center justify-center p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors duration-200"
              aria-label="Go back"
            >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">{t.title}</h1>
            <p className="text-sm text-gray-500">{t.subtitle}</p>
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="px-4 pb-4">
        <div className="flex bg-gray-50 rounded-xl p-1 w-full">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chats List */}
      <div className="px-4 pb-20">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
            <span className="ml-2 text-gray-600">{t.loading}</span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              onClick={loadChats}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              {t.retry}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredChats.length > 0 ? (
              filteredChats.map((chat, index) => (
              <motion.div
                key={chat.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 p-4"
                >
                  <div className="flex items-start gap-4">
                    {/* Profile Picture */}
                    <div className="flex-shrink-0 relative">
                      <img 
                        src={chat.lawyer.profilePic} 
                        alt={chat.lawyer.name} 
                        className="w-16 h-16 rounded-full object-cover border-2 border-gray-100"
                      />
                      {chat.unreadCount > 0 && (
                        <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center">
                          <span className="text-white text-xs font-bold">{chat.unreadCount}</span>
                        </div>
                      )}
                    </div>

                    {/* Chat Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 mb-1">{chat.lawyer.name}</h3>
                          <p className="text-sm font-medium text-purple-600 mb-1">{chat.lawyer.specialization}</p>
                        </div>
                        <div className="text-right relative">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            chat.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {chat.statusLabel}
                          </span>
                          <p className="text-gray-500 text-xs mt-1">{chat.lastMessageTime}</p>
                          
                          {/* Menu Button */}
                          <button
                            onClick={(e) => handleMenuClick(e, chat.id)}
                            className="p-1 hover:bg-gray-100 rounded-full transition-colors duration-200 mt-1"
                          >
                            <MoreVertical className="w-4 h-4 text-gray-500" />
                          </button>
                          
                          {/* Dropdown Menu */}
                          {showMenu === chat.id && (
                            <div className="absolute right-0 top-8 z-10 bg-white rounded-lg shadow-lg border border-gray-200 py-2 min-w-[160px]">
                              <button
                                onClick={(e) => handleDeleteChat(chat.id)}
                                className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200"
                              >
                                <Trash2 className="w-4 h-4" />
                                {t.deleteChat}
                              </button>
                            </div>
                          )}
                        </div>
                      </div>

                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                          {chat.lastMessage}
                        </p>

                      {/* Action Button */}
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleViewChat(chat)}
                          className="w-full bg-purple-600 text-white py-3 px-4 rounded-xl font-medium text-sm hover:bg-purple-700 transition-colors"
                        >
                          {t.viewChat}
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">💬</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t.noChats}</h3>
                <p className="text-gray-500 mb-6">{t.noChatsDesc}</p>
                <button
                  onClick={() => navigate('/find-lawyer')}
                  className="bg-purple-600 text-white py-3 px-6 rounded-xl font-medium hover:bg-purple-700 transition-colors"
                >
                  Find a Lawyer
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Click outside to close menu */}
      {showMenu && (
        <div 
          className="fixed inset-0 z-0" 
          onClick={() => setShowMenu(null)}
        />
      )}
    </div>
  );
};

export default ChatDetail;